import type {
  Agreement,
  AgreementRef,
  AutogiroPaymentSettings,
  DebtorDeliveryMethod,
  DebtorPaymentMethod,
  DeviceConnection,
  EInvoiceBank,
  EInvoiceCompany,
  EmailDelivery,
  M46Remote,
  MailDelivery,
  PaymentTerms,
  Personnummer,
  PriceIndexMethod,
  QueueApplicantTag,
  QueueApplicantVolition,
  SmsDelivery,
  SwedishId,
} from "../../apis/parkito/index.ts";

export function checkLuhn(n: number): boolean {
  if (n < 10) {
    return false;
  }

  let sum = 0;
  let fac = 1;

  while (0 < n) {
    const product = (n % 10) * fac;

    if (9 < product) {
      sum = sum + (product % 10) + Math.floor(product / 10);
    } else {
      sum = sum + product;
    }

    if (fac == 1) {
      fac = 2;
    } else {
      fac = 1;
    }

    n = Math.floor(n / 10);
  }

  return sum % 10 === 0;
}

export function isPersonVariant(
  swedishId: SwedishId,
): swedishId is { person: Personnummer } {
  return "person" in swedishId;
}

export function isRemoteVariant(
  remoteId: DeviceConnection,
): remoteId is { m46Remote: M46Remote } {
  return "m46Remote" in remoteId;
}

export function isDaysVariant(
  paymentTerms: PaymentTerms,
): paymentTerms is { day: number } {
  return typeof paymentTerms === "object" && "days" in paymentTerms;
}

// I hate the way this following 3 methods are written, but I'm not
// sure how to improve it. Nested type guards are a pain.
export function isPriceIndexingMethodMonthly(
  method: PriceIndexMethod,
): method is { monthly: { percent: number } } {
  return typeof method === "object" && "monthly" in method;
}

export function isPriceIndexingMethodQuarterly(
  method: PriceIndexMethod,
): method is { quarterly: { percent: number } } {
  return typeof method === "object" && "quarterly" in method;
}

export function isPriceIndexingMethodYearly(
  method: PriceIndexMethod,
): method is { yearly: { percent: number } } {
  return typeof method === "object" && "yearly" in method;
}

export function getPriceIndexingType(
  method: PriceIndexMethod | undefined,
): "baseIndex" | "monthly" | "quarterly" | "yearly" | undefined {
  if (typeof method === "string" && method === "baseIndex") {
    return "baseIndex";
  } else if (typeof method === "object") {
    if ("monthly" in method) {
      return "monthly";
    } else if ("quarterly" in method) {
      return "quarterly";
    } else if ("yearly" in method) {
      return "yearly";
    }
  }
  return undefined; // No valid type found
}

export function getPriceIndexingPercent(
  method: PriceIndexMethod | undefined,
): number | undefined {
  if (typeof method === "string" && method === "baseIndex") {
    return undefined;
  } else if (typeof method === "object") {
    if ("monthly" in method) {
      return method.monthly.percent;
    } else if ("quarterly" in method) {
      return method.quarterly.percent;
    } else if ("yearly" in method) {
      return method.yearly.percent;
    }
  }
  return undefined; // No valid type found
}

// 5 type checking function used for DebtorDeliveryMethod
export function deliveryMethodIsMail(
  method: any,
): method is { mail: MailDelivery } {
  return typeof method === "object" && "mail" in method;
}

export function deliveryMethodIsEmail(
  method: any,
): method is { email: EmailDelivery } {
  return typeof method === "object" && "email" in method;
}

export function deliveryMethodIsSms(
  method: any,
): method is { sms: SmsDelivery } {
  return typeof method === "object" && "sms" in method;
}

export function deliveryMethodIsEInvoicePerson(
  method: any,
): method is { eInvoicePerson: EInvoiceBank } {
  return typeof method === "object" && "eInvoicePerson" in method;
}

export function deliveryMethodIsEInvoiceCompany(
  method: any,
): method is { eInvoiceCompany: EInvoiceCompany } {
  return typeof method === "object" && "eInvoiceCompany" in method;
}

export function getDeliveryMethodType(
  method: DebtorDeliveryMethod | null | undefined,
): string | undefined {
  if (typeof method === "string") {
    return method; // 'none' or 'kivra'
  } else if (method) {
    if (deliveryMethodIsMail(method)) {
      // Perform actions specific to MailDelivery
      return "mail";
    } else if (deliveryMethodIsEmail(method)) {
      // Perform actions specific to EmailDelivery
      return "email";
    } else if (deliveryMethodIsSms(method)) {
      // Perform actions specific to SmsDelivery
      return "sms";
    } else if (deliveryMethodIsEInvoicePerson(method)) {
      // Perform actions specific to EInvoiceBank
      return "eInvoicePerson";
    } else if (deliveryMethodIsEInvoiceCompany(method)) {
      // Perform actions specific to EInvoiceCompany
      return "eInvoiceCompany";
    }
  }
  return undefined;
}

export function paymentMethodIsInvoice(method: any): method is "invoice" {
  return typeof method === "object" && "invoice" in method;
}

export function paymentMethodIsAutogiro(
  method: any,
): method is { autogiro: AutogiroPaymentSettings } {
  return typeof method === "object" && "autogiro" in method;
}

export function getPaymentMethodType(
  method: DebtorPaymentMethod | null | undefined,
): string | undefined {
  if (typeof method === "string") {
    return method; // 'none'
  } else if (method) {
    if (paymentMethodIsInvoice(method)) {
      // Perform actions specific to invoice
      return "invoice";
    } else if (paymentMethodIsAutogiro(method)) {
      // Perform actions specific to AutogiroPaymentSettings
      return "autogiro";
    }
  }
  return undefined;
}

export function volitionTypeIsFirst(
  method: any,
): method is { first: { tags: QueueApplicantTag[] } } {
  return typeof method === "object" && "first" in method;
}

export function volitionTypeIsMore(
  method: any,
): method is { more: { tags: QueueApplicantTag[] } } {
  return typeof method === "object" && "more" in method;
}

export function volitionTypeIsSwapToTags(method: any): method is {
  swapToTags: {
    fromAgreementSpotId: string;
    tags: { tagId: string }[];
  };
} {
  return typeof method === "object" && "swapToTags" in method;
}

export function volitionTypeIsSwapToSpots(method: any): method is {
  swapToSpots: {
    fromAgreementSpotId: string;
    spotIds: string[];
  };
} {
  return typeof method === "object" && "swapToSpots" in method;
}

export function getVolitionType(
  volition: QueueApplicantVolition | null | undefined,
): string | undefined {
  if (typeof volition === "string") {
    return volition; // 'none'
  } else if (volition) {
    if (volitionTypeIsFirst(volition)) {
      return "first";
    } else if (volitionTypeIsMore(volition)) {
      return "more";
    } else if (volitionTypeIsSwapToSpots(volition)) {
      return "swapToSpots";
    } else if (volitionTypeIsSwapToTags(volition)) {
      return "swapToTags";
    }
  }
  return undefined;
}

export function isAgreementRef(
  agreement: AgreementRef | Agreement,
): agreement is AgreementRef {
  return (agreement as AgreementRef).startDate !== undefined;
}

export function subjectTypeIsClient(method: any): method is { client: string } {
  return typeof method === "object" && "client" in method;
}

export function subjectTypeIsSite(method: any): method is { site: string } {
  return typeof method === "object" && "site" in method;
}
